import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './button';

/**
 * Modal de confirmação reutilizável
 * Substitui o window.confirm() nativo com um modal bonito
 * Sem dependências externas - apenas CSS e React
 */
export function ConfirmationDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'default', // 'default', 'destructive'
  confirmDisabled = false,
  children,
}) {
  // Fechar com ESC
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isOpen, onClose]);

  // Prevenir scroll do body quando modal está aberto
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Portal: escapa do fluxo normal da página (containers com space-y-* etc.
  // deslocariam o overlay fixed, deixando uma faixa sem cobrir no topo).
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal — max-h + flex-col garante que o rodapé de ações nunca fica inacessível:
          em telas baixas (celular em paisagem, ou conteúdo/descrição longos), só o
          corpo rola internamente, os botões continuam sempre visíveis. */}
      <div
        className="relative bg-white rounded-lg shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Corpo rolável: título + descrição + conteúdo extra */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {title}
            </h2>
          </div>

          <div className={children ? 'mb-4' : ''}>
            <p className="text-sm text-gray-600">
              {description}
            </p>
          </div>

          {/* Conteúdo extra opcional (ex.: campo de notas) */}
          {children}
        </div>

        {/* Footer — fixo, sempre visível independente do tamanho do conteúdo acima */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-6 pt-4 border-t border-gray-100 shrink-0">
          <Button
            variant="outline"
            onClick={onClose}
            className="w-full sm:w-auto"
          >
            {cancelText}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={confirmDisabled}
            className={
              variant === 'destructive'
                ? 'bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed'
                : 'w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed'
            }
          >
            {confirmText}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
